import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Let Supabase handle the auth state, but we need to handle the redirect
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        // User is successfully signed in
        // Get the redirect destination from sessionStorage (preserved during OAuth)
        // or from URL query parameters (for email confirmation flows)
        let redirectTo = sessionStorage.getItem('authRedirectTo');

        // Mobile fallback: If sessionStorage is empty, try localStorage
        if (!redirectTo) {
          redirectTo = localStorage.getItem('authRedirectTo');
          if (redirectTo) {
            // Clean up localStorage if we found the redirect there
            localStorage.removeItem('authRedirectTo');
          }
        } else {
          // Clean up the stored redirect destination
          sessionStorage.removeItem('authRedirectTo');
        }

        if (!redirectTo) {
          // Check URL query parameters for email confirmation flows
          const searchParams = new URLSearchParams(location.search);
          redirectTo = searchParams.get('redirectTo') || '/dashboard';
        }

        // If the user has no explicit destination (landed on /dashboard by default),
        // route by role instead. OAuth and email-confirmation sign-ins land here and
        // never pass through PublicRoute, so this is the only role check they get —
        // without the staff branch a teacher with no parents row would be sent to
        // /dashboard and then bounced into parent onboarding. Admin wins over staff,
        // matching PublicRoute.
        if (redirectTo === '/dashboard') {
          (async () => {
            const [{ data: isAdmin }, { data: isStaff }] = await Promise.all([
              supabase.rpc('is_admin'),
              supabase.rpc('is_staff'),
            ]);
            const destination = isAdmin ? '/admin-app' : isStaff ? '/staff-app' : '/dashboard';
            navigate(destination, { replace: true });
          })();
          return;
        }

        navigate(redirectTo, { replace: true });
      } else if (session === null) {
        // Check if there's an error in the URL
        const error = new URLSearchParams(window.location.search).get('error_description');
        if (error?.includes('Email not confirmed')) {
          navigate('/login', { 
            replace: true,
            state: { needsConfirmation: true }
          });
        } else {
          // No session found or other error, redirect to login
          navigate('/login', { replace: true });
        }
      }
      // If session is undefined, we're still loading, so don't navigate yet
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <div className="text-xl text-gray-600">Completing sign in...</div>
      </div>
    </div>
  );
} 